"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "LogReporter", {
    enumerable: true,
    get: function() {
        return LogReporter;
    }
});
const _formathrtime = require("@lage-run/format-hrtime");
const _isTargetStatusLogEntry = require("./isTargetStatusLogEntry.js");
const _logger = require("@lage-run/logger");
const _ansiregex = /*#__PURE__*/ _interop_require_default(require("ansi-regex"));
const _chalk = /*#__PURE__*/ _interop_require_default(require("chalk"));
const _gradientstring = /*#__PURE__*/ _interop_require_default(require("gradient-string"));
const _crypto = /*#__PURE__*/ _interop_require_default(require("crypto"));
const _formatBytes = require("./formatBytes.js");
const _slowestTargetRuns = require("./slowestTargetRuns.js");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
const colors = {
    [_logger.LogLevel.info]: _chalk.default.white,
    [_logger.LogLevel.verbose]: _chalk.default.gray,
    [_logger.LogLevel.warn]: _chalk.default.white,
    [_logger.LogLevel.error]: _chalk.default.hex("#FF1010"),
    [_logger.LogLevel.silly]: _chalk.default.green,
    task: _chalk.default.hex("#00DDDD"),
    pkg: _chalk.default.hex("#FFD66B"),
    ok: _chalk.default.green,
    error: _chalk.default.red,
    warn: _chalk.default.yellow
};
// Monokai color scheme
const pkgColors = [
    _chalk.default.hex("#e5b567"),
    _chalk.default.hex("#b4d273"),
    _chalk.default.hex("#e87d3e"),
    _chalk.default.hex("#9e86c8"),
    _chalk.default.hex("#b05279"),
    _chalk.default.hex("#6c99bb")
];
function hashStringToNumber(str) {
    const hash = _crypto.default.createHash("md5");
    hash.update(str);
    const hex = hash.digest("hex").substring(0, 6);
    return parseInt(hex, 16);
}
const pkgNameToIndexInPkgColorArray = new Map();
function getColorForPkg(pkg) {
    if (!pkgNameToIndexInPkgColorArray.has(pkg)) {
        const index = hashStringToNumber(pkg) % pkgColors.length;
        pkgNameToIndexInPkgColorArray.set(pkg, index);
    }
    return pkgColors[pkgNameToIndexInPkgColorArray.get(pkg)];
}
const stripAnsiRegex = (0, _ansiregex.default)();
function getTaskLogPrefix(pkg, task) {
    const pkgColor = getColorForPkg(pkg);
    return `${pkgColor(pkg)} ${colors.task(task)}`;
}
function stripAnsi(message) {
    return message.replace(stripAnsiRegex, "");
}
function normalize(prefixOrMessage, message) {
    if (typeof message === "string") {
        const prefix = prefixOrMessage;
        return {
            prefix,
            message
        };
    } else {
        const prefix = "";
        const message = prefixOrMessage;
        return {
            prefix,
            message
        };
    }
}
class LogReporter {
    log(entry) {
        // if "hidden", do not even attempt to record or report the entry
        if (entry?.data?.target?.hidden) {
            return;
        }
        // save the logs for errors
        if (entry.data?.target?.id) {
            if (!this.logEntries.has(entry.data.target.id)) {
                this.logEntries.set(entry.data.target.id, []);
            }
            this.logEntries.get(entry.data.target.id).push(entry);
        }
        // if loglevel is not high enough, do not report the entry
        if (this.options.logLevel < entry.level) {
            return;
        }
        // log to grouped entries
        if (this.options.grouped && entry.data?.target) {
            return this.logTargetEntryByGroup(entry);
        }
        // log normal target entries
        if (entry.data && entry.data.target) {
            return this.logTargetEntry(entry);
        }
        // log generic entries (not related to target)
        if (entry.msg) {
            return this.print(entry.msg);
        }
    }
    printEntry(entry, message) {
        let prefix = "";
        let msg = message;
        if (entry?.data?.target) {
            const { packageName, task } = entry.data.target;
            const normalizedArgs = normalize(getTaskLogPrefix(packageName ?? "<root>", task), msg);
            prefix = normalizedArgs.prefix;
            msg = normalizedArgs.message;
        }
        this.print(`${prefix ? prefix + " " : ""}${msg}`);
    }
    print(message) {
        this.logStream.write(message + "\n");
    }
    logTargetEntry(entry) {
        const colorFn = colors[entry.level];
        const data = entry.data;
        if ((0, _isTargetStatusLogEntry.isTargetStatusLogEntry)(data)) {
            const { hash, duration } = data;
            switch(data.status){
                case "running":
                    return this.printEntry(entry, colorFn(`${colors.ok("➔")} start`));
                case "success":
                    return this.printEntry(entry, colorFn(`${colors.ok("✓")} done - ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(duration))}`));
                case "failed":
                    return this.printEntry(entry, colorFn(`${colors.error("✖")} fail`));
                case "skipped":
                    return this.printEntry(entry, colorFn(`${colors.ok("»")} skip - ${hash}`));
                case "aborted":
                    return this.printEntry(entry, colorFn(`${colors.warn("-")} aborted`));
                case "queued":
                    return this.printEntry(entry, colorFn(`${colors.warn("…")} queued`));
            }
        } else {
            return this.printEntry(entry, colorFn(":  " + stripAnsi(entry.msg)));
        }
    }
    logTargetEntryByGroup(entry) {
        const data = entry.data;
        const target = data.target;
        const { id } = target;
        if ((0, _isTargetStatusLogEntry.isTargetStatusLogEntry)(data) && (data.status === "success" || data.status === "failed" || data.status === "skipped" || data.status === "aborted")) {
            const entries = this.logEntries.get(id);
            for (const targetEntry of entries){
                this.logTargetEntry(targetEntry);
            }
            if (entries.length > 2) {
                this.hr();
            }
        }
    }
    hr() {
        this.print("┈".repeat(80));
    }
    summarize(schedulerRunSummary) {
        const { targetRuns, targetRunByStatus, duration } = schedulerRunSummary;
        const { failed, aborted, skipped, success, pending } = targetRunByStatus;
        const statusColorFn = {
            success: _chalk.default.greenBright,
            failed: _chalk.default.redBright,
            skipped: _chalk.default.gray,
            running: _chalk.default.yellow,
            pending: _chalk.default.gray,
            aborted: _chalk.default.red,
            queued: _chalk.default.magenta
        };
        if (targetRuns.size > 0) {
            this.print(_chalk.default.cyanBright(`\nSummary`));
            this.hr();
            const slowestTargets = (0, _slowestTargetRuns.slowestTargetRuns)([
                ...targetRuns.values()
            ]);
            for (const wrappedTarget of slowestTargets){
                if (wrappedTarget.target.hidden) {
                    continue;
                }
                const colorFn = statusColorFn[wrappedTarget.status] ?? _chalk.default.white;
                const target = wrappedTarget.target;
                const hasDurations = !!wrappedTarget.duration && !!wrappedTarget.queueTime;
                const queueDuration = hasDurations ? (0, _formathrtime.hrtimeDiff)(wrappedTarget.queueTime, wrappedTarget.startTime) : [
                    0,
                    0
                ];
                this.print(`${getTaskLogPrefix(target.packageName || "<root>", target.task)} ${colorFn(`${wrappedTarget.status === "running" ? "running - incomplete" : wrappedTarget.status}${hasDurations ? `, took ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(wrappedTarget.duration))}, queued for ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(queueDuration))}` : ""}`)}`);
            }
            this.print(`success: ${success.length}, skipped: ${skipped.length}, pending: ${pending.length}, aborted: ${aborted.length}, failed: ${failed.length}`);
            this.print(`worker restarts: ${schedulerRunSummary.workerRestarts}, max worker memory usage: ${(0, _formatBytes.formatBytes)(schedulerRunSummary.maxWorkerMemoryUsage)}`);
        } else {
            this.print("Nothing has been run.");
        }
        this.hr();
        if (failed && failed.length > 0) {
            for (const targetId of failed){
                const target = targetRuns.get(targetId)?.target;
                if (target) {
                    const { packageName, task } = target;
                    const failureLogs = this.logEntries.get(targetId);
                    this.print(`[${colors.pkg(packageName ?? "<root>")} ${colors.task(task)}] ${colors[_logger.LogLevel.error]("ERROR DETECTED")}`);
                    if (failureLogs) {
                        for (const entry of failureLogs){
                            // Log each entry separately to prevent truncation
                            this.print(entry.msg);
                        }
                    }
                    this.hr();
                }
            }
        }
        const allCacheHits = [
            ...targetRuns.values()
        ].filter((run)=>!run.target.hidden).length === skipped.length;
        const allCacheHitText = allCacheHits ? (0, _gradientstring.default)({
            r: 237,
            g: 178,
            b: 77
        }, "cyan")(`All targets skipped!`) : "";
        this.print(`Took a total of ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(duration))} to complete. ${allCacheHitText}`);
    }
    resetLogEntries() {
        this.logEntries.clear();
    }
    constructor(options){
        _define_property(this, "options", void 0);
        _define_property(this, "logStream", void 0);
        _define_property(this, "logEntries", void 0);
        _define_property(this, "groupedEntries", void 0);
        this.options = options;
        this.logStream = process.stdout;
        this.logEntries = new Map();
        this.groupedEntries = new Map();
        options.logLevel = options.logLevel || _logger.LogLevel.info;
    }
}
